import React, { useEffect, useState } from 'react';
import ArticleForm from '../components/Forms/ArticleForm';
import ArticlePrixDepartementForm from '../components/Forms/ArticlePrixDepartementForm';
import NomenclatureEditor from '../components/Forms/NomenclatureEditor';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../reduxStore/Store';
import { fetchArticleByFilters, setSelectedArticle } from '../reduxStore/ArticleSlice';
import { Article } from '../models/models';
import { ButtonType, GeneriqueDataType, PERMISSION_PRIX_DEPT } from '../constants/enums';
import { usePermission } from '../hooks/usePermission';
import { TableAction, TableHeader } from '../constants/interfaces';
import MyTable from '../components/MyTable';
import { getLabels } from '../constants/fieldLabels';
import { Box, Button, Checkbox, Collapse, DialogTitle, Tab, Tabs, Typography } from '@mui/material';
import { useDialog } from '../components/DialogComponent';
import FamilleHierarchie from './FamilleHierarchie';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppOutlineButton from '../components/layout/AppOutlineButton';
import ArticleFilter, { ArticleFilterValues } from '../components/Filters/ArticleFilter';
import ImportArticlesDialog from '../components/Forms/ImportArticlesDialog';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useNavigate } from 'react-router-dom';


const GestionArticle: React.FC = () => {

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { articleData, total, loading, error, successMessage } = useSelector((state: any) => state.ArticleSlice);
    const selectedArticle = useSelector((state: any) => state.ArticleSlice.selectedArticle) as Article | null;
    const selectedDepartement = useSelector((state: any) => state.AppSlice.selectedDepartement);
    // Mono-département : un seul dept autorisé → "ce département" et "Tous les
    // départements" représentent exactement les mêmes données (même règle que
    // AllModeRequiredRoute/useDepartementBootstrap) : l'édition des défauts
    // tenant reste donc accessible, il n'y a pas d'autre contexte à écraser.
    const allowedDepartements = useSelector((state: any) => state.ScopeSlice.allowedDepartements);
    const isMonoDepartement = allowedDepartements.length === 1;
    // Sécurité : en mode dept (2+ départements), l'édition d'un article masque
    // les tabs "Article" (édition des défauts tenant) et "Nomenclature" (donnée
    // tenant-level). Seul le tab "Prix par département" reste accessible —
    // toute autre modif doit se faire en mode ALL pour éviter les écrasements
    // accidentels du master tenant depuis un contexte dept-spécifique.
    // Même règle sans prix de la fiche (*_defaut à NULL, 0153 : user restreint à un
    // département d'un tenant qui en a plusieurs) : ArticleForm retomberait sur le
    // prix du département et l'écrirait sur la fiche.
    const seulementPrixDept = (article: Article | null): boolean =>
        !!article && !!selectedDepartement && (!isMonoDepartement || article.prix_v_defaut == null);
    const onlyDeptPriceTab = seulementPrixDept(selectedArticle);
    // Onglet « Prix par département » : droit prixDepartements.read (0154)
    const peutVoirPrixDept = usePermission(PERMISSION_PRIX_DEPT.READ);
    const emptyFilters: ArticleFilterValues = { code: null, designation: null, famille_id: null, fournisseur_id: null, visible: null, partage: [] };
    const [filterValues, setFilterValues] = useState<ArticleFilterValues>(emptyFilters);
    const [fetchParams, setFetchParams] = useState<{ limit: number; offset: number } & ArticleFilterValues>(
        { limit: 10, offset: 0, ...emptyFilters }
    );
    const [showHierarchie, setShowHierarchie] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [FormDialog, openDialog, closeDialog] = useDialog({
        dialogProps: { maxWidth: 'md', fullWidth: true }
    });
    const [importOpen, setImportOpen] = useState(false);

    const handleCancel = () => {
        closeDialog();
        dispatch(setSelectedArticle(null));
        setActiveTab(0);
    };

    const openForNew = () => {
        dispatch(setSelectedArticle(null));
        setActiveTab(0);
        openDialog();
    };

    const openForEdit = (article: Article) => {
        dispatch(setSelectedArticle(article));
        // Tab "Article" caché (cf. seulementPrixDept) → défaut sur "Prix par département".
        setActiveTab(seulementPrixDept(article) ? 1 : 0);
        openDialog();
    };

    // Après save/suppression d'un override : recharge la liste et l'article ouvert.
    // Sans prix de la fiche (0153), le formulaire repart du prix effectif de l'article.
    const handlePrixSaved = async () => {
        const action = await dispatch(fetchArticleByFilters(fetchParams));
        if (!fetchArticleByFilters.fulfilled.match(action)) return;
        const article = action.payload.data.find((a) => a.id === selectedArticle?.id);
        if (article) dispatch(setSelectedArticle(article));
    };

    useEffect(() => {
        dispatch(fetchArticleByFilters(fetchParams));
    }, [dispatch, fetchParams, successMessage]);

    /* ── Colonnes essentielles uniquement (6 = maximum) — le fournisseur (0143)
          est consultable via le dialog de détail (showDetails / fieldLabels) ── */
    const headers: TableHeader<Article>[] = [
        { key: 'code',        label: 'Code' },
        { key: 'designation', label: 'Désignation' },
        { key: 'prix_v',      label: 'Prix vente HT' },
        { key: 'famille_nom', label: 'Famille' },
        {
            key: 'departements_partage_noms', label: 'Départements', hideBelow: 'md',
            render: (row) => row.departements_partage_noms || 'Tous',
        },
        {
            key: 'visible', label: 'Visible',
            render: (row) => <Checkbox checked={row.visible} disabled color="primary" size="small" />
        },
    ];

    /* ── Action unique : Edit (ouvre le dialog avec onglets) ──
          Masquée quand seul l'onglet des prix département serait ouvert sans le droit de le voir. */
    const tableActions: TableAction<Article>[] = [
        {
            label: 'Edit',
            callback: openForEdit,
            type: ButtonType.EDIT,
            hidden: (row) => seulementPrixDept(row) && !peutVoirPrixDept,
        },
    ];

    const handleFilterChange = (values: ArticleFilterValues) => {
        setFilterValues(values);
        setFetchParams(prev => ({ ...prev, ...values, offset: 0 }));
    };

    const handleFilterReset = () => {
        setFilterValues(emptyFilters);
        setFetchParams(prev => ({ ...prev, ...emptyFilters, offset: 0 }));
    };

    const handlePageChange = (newPage: number, newRowsPerPage: number) => {
        setFetchParams(prev => ({ ...prev, offset: newPage * newRowsPerPage, limit: newRowsPerPage }));
    };

    // Tabs 1 et 2 (Prix / Nomenclature) nécessitent un article persisté (id défini)
    const articleSaved = !!selectedArticle?.id;

    return (
        <AppPage maxWidth={false}>
            <AppPageHeader
                title="Gestion Articles"
                subtitle={`${total} articles enregistrés`}
                icon={<InventoryIcon />}
                addLabel="Ajouter un Article"
                onAdd={openForNew}
                actions={
                    <Box display="flex" gap={1}>
                    <AppOutlineButton
                        startIcon={<FileUploadIcon />}
                        onClick={() => setImportOpen(true)}
                    >
                        Importer Excel
                    </AppOutlineButton>
                    <AppOutlineButton
                        startIcon={<AutoFixHighIcon />}
                        onClick={() => navigate('/article/masse')}
                    >
                        Actions par famille
                    </AppOutlineButton>
                    <AppOutlineButton
                        startIcon={<AccountTreeIcon />}
                        onClick={() => setShowHierarchie(prev => !prev)}
                    >
                        {showHierarchie ? 'Masquer familles' : 'Familles'}
                    </AppOutlineButton>
                    </Box>
                }
            />

            <ImportArticlesDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={() => {
                    setImportOpen(false);
                    dispatch(fetchArticleByFilters(fetchParams));
                }}
            />

            {error && <Typography color="#b42318" mb={2}>Erreur : {error}</Typography>}

            <Collapse in={showHierarchie}>
                <AppSurface sx={{ mb: 2 }}><FamilleHierarchie type={GeneriqueDataType.FAMILLE_ARTICLE} /></AppSurface>
            </Collapse>

            <AppSurface sx={{ mb: 2 }}>
                <ArticleFilter
                    values={filterValues}
                    onChange={handleFilterChange}
                    onReset={handleFilterReset}
                />
            </AppSurface>

            <AppSurface>
                <MyTable
                    headers={headers}
                    data={articleData}
                    onPageChange={handlePageChange}
                    actions={tableActions}
                    totalRows={total}
                    loading={loading}
                    defaultPageSize={fetchParams.limit}
                    showDetails
                    fieldLabels={getLabels('Article')}
                    booleanFields={['track_stock', 'allow_pcs_cts']}
                />
            </AppSurface>

            {/* ── Dialog unique avec 3 onglets ── */}
            <FormDialog>
                <DialogTitle>
                    <Typography variant="h6">
                        {selectedArticle ? `Article — ${selectedArticle.code ?? ''}` : 'Ajouter un Article'}
                    </Typography>
                </DialogTitle>

                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
                >
                    {!onlyDeptPriceTab && <Tab label="Article" value={0} />}
                    {peutVoirPrixDept && (
                        <Tab
                            label="Prix par département"
                            value={1}
                            disabled={!articleSaved}
                        />
                    )}
                    {!onlyDeptPriceTab && (
                        <Tab
                            label="Nomenclature"
                            value={2}
                            disabled={!articleSaved}
                        />
                    )}
                </Tabs>

                <Box sx={{ p: 2 }}>
                    {activeTab === 0 && !onlyDeptPriceTab && (
                        <ArticleForm onCancel={handleCancel} />
                    )}

                    {activeTab === 1 && peutVoirPrixDept && articleSaved && selectedArticle && (
                        <>
                            <ArticlePrixDepartementForm
                                article={selectedArticle}
                                onSaved={handlePrixSaved}
                            />
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button onClick={handleCancel} variant="outlined">Fermer</Button>
                            </Box>
                        </>
                    )}

                    {activeTab === 2 && !onlyDeptPriceTab && articleSaved && selectedArticle && (
                        <>
                            <NomenclatureEditor parent={selectedArticle} />
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button onClick={handleCancel} variant="outlined">Fermer</Button>
                            </Box>
                        </>
                    )}
                </Box>
            </FormDialog>

        </AppPage>
    );
};

export default GestionArticle;
